import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import '../styles/Emails.css';
import { authStorage } from '../services/authStorage';
import { authApi, ApiError } from '../services/authApi';
import { User, Company } from '../types/auth';
import { getAvatarUrl } from '../utils/avatarUtils';
import { useToast } from '../contexts/ToastContext';

interface UserWithCompany extends User {
  companyName?: string;
}

interface EmailPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  subject: string;
  htmlBody: string;
  selectedUsers: UserWithCompany[];
  sendAsGroup: boolean;
  onSend: () => void;
  sending: boolean;
}

interface EmailsProps {
  currentUser?: {
    username: string;
    role: 'user' | 'admin';
  };
}

const EmailPreviewModal: React.FC<EmailPreviewModalProps> = ({
  isOpen,
  onClose,
  subject,
  htmlBody,
  selectedUsers,
  sendAsGroup,
  onSend,
  sending
}) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  const recipientEmails = selectedUsers.map(user => user.email);

  return (
    <div className="email-modal-backdrop" onClick={onClose}>
      <div className="email-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="email-modal-header">
          <h2>Email Preview</h2>
          <button className="email-modal-close" onClick={onClose}>&times;</button>
        </div>
        
        <div className="email-modal-body">
          <div className="email-preview-section">
            <h3>Subject</h3>
            <div className="email-preview-subject">{subject}</div>
          </div>

          <div className="email-preview-section">
            <h3>Recipients ({selectedUsers.length})</h3>
            <div className="email-preview-recipients">
              {selectedUsers.map(user => (
                <div key={user.id} className="email-recipient-item">
                  <img src={getAvatarUrl(user.profileImageUrl, user.username)} alt={user.username} />
                  <div className="recipient-info">
                    <span className="recipient-username">{user.username}</span>
                    <span className="recipient-email">{user.email}</span>
                    {user.companyName && <span className="recipient-company">{user.companyName}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="email-preview-section">
            <h3>Sending Method</h3>
            <div className="email-sending-method">
              {sendAsGroup ? 'Send as group email (all recipients in To field)' : 'Send as individual emails (separate email to each recipient)'}
            </div>
          </div>

          <div className="email-preview-section">
            <h3>Message Content</h3>
            <div className="email-preview-body" dangerouslySetInnerHTML={{ __html: htmlBody }} />
          </div>
        </div>

        <div className="email-modal-footer">
          <button className="email-cancel-button" onClick={onClose} disabled={sending}>
            Cancel
          </button>
          <button className="email-send-button" onClick={onSend} disabled={sending || !subject.trim() || !htmlBody.trim() || selectedUsers.length === 0}>
            {sending ? 'Sending...' : 'Send Email'}
          </button>
        </div>
      </div>
    </div>
  );
};

const Emails: React.FC<EmailsProps> = ({ currentUser }) => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [users, setUsers] = useState<UserWithCompany[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  
  // Form state
  const [subject, setSubject] = useState('');
  const [htmlBody, setHtmlBody] = useState('');
  const [sendAsGroup, setSendAsGroup] = useState(false);
  
  // User selection state
  const [selectedUserIds, setSelectedUserIds] = useState<Set<number>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [selectAll, setSelectAll] = useState(false);
  
  // Modal state
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [sending, setSending] = useState(false);

  // Rich text editor modules configuration
  const modules = useMemo(() => ({
    toolbar: [
      [{ 'header': [1, 2, false] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }, { 'indent': '-1'}, { 'indent': '+1' }],
      ['link', 'image'],
      ['clean']
    ],
  }), []);

  const formats = [
    'header', 'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet', 'indent', 'link', 'image'
  ];

  useEffect(() => {
    loadUsersAndCompanies();
  }, []);

  const loadUsersAndCompanies = async () => {
    try {
      setLoading(true);
      setError('');
      const token = authStorage.getAccessToken();
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        return;
      }

      const [usersResponse, companiesResponse] = await Promise.all([
        authApi.getAllUsers(token),
        authApi.getAllCompanies(token)
      ]);

      // Create a map of companyId to company name
      const companyMap = new Map(companiesResponse.map(company => [company.id, company.companyName]));
      
      // Add company names to users
      const usersWithCompany: UserWithCompany[] = usersResponse.map(user => ({
        ...user,
        companyName: user.companyId ? companyMap.get(user.companyId) : 'Admin'
      }));

      setUsers(usersWithCompany);
      setCompanies(companiesResponse);
    } catch (error) {
      console.error('Failed to load users and companies:', error);
      if (error instanceof ApiError) {
        setError(error.message);
      } else {
        setError('Failed to load users and companies. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Filter users based on search term
  const filteredUsers = useMemo(() => {
    if (!searchTerm) return users;
    
    const search = searchTerm.toLowerCase();
    return users.filter(user => 
      user.username.toLowerCase().includes(search) ||
      user.email.toLowerCase().includes(search) ||
      (user.companyName && user.companyName.toLowerCase().includes(search))
    );
  }, [users, searchTerm]);

  // Get selected users for preview
  const selectedUsers = useMemo(() => {
    return users.filter(user => selectedUserIds.has(user.id));
  }, [users, selectedUserIds]);

  // Handle user selection
  const handleUserSelect = (userId: number, checked: boolean) => {
    const newSelection = new Set(selectedUserIds);
    if (checked) {
      newSelection.add(userId);
    } else {
      newSelection.delete(userId);
    }
    setSelectedUserIds(newSelection);
    
    // Update select all state
    setSelectAll(newSelection.size === filteredUsers.length && filteredUsers.length > 0);
  };

  // Handle select all
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allUserIds = new Set(filteredUsers.map(user => user.id));
      setSelectedUserIds(allUserIds);
    } else {
      setSelectedUserIds(new Set());
    }
    setSelectAll(checked);
  };

  // Handle email sending
  const handleSendEmail = async () => {
    try {
      setSending(true);
      const token = authStorage.getAccessToken();
      if (!token) {
        showError('Authentication token not found. Please log in again.');
        return;
      }

      const recipients = selectedUsers.map(user => user.email);
      
      const emailData = {
        subject: subject,
        recipients: recipients,
        htmlBody: htmlBody,
        sendAsBulk: sendAsGroup
      };

      // Call the email API - we'll add this to authApi
      await authApi.sendEmail(token, emailData);
      
      showSuccess(`Email sent successfully to ${recipients.length} recipient${recipients.length > 1 ? 's' : ''}!`);
      
      // Reset form
      setSubject('');
      setHtmlBody('');
      setSelectedUserIds(new Set());
      setSelectAll(false);
      setIsPreviewModalOpen(false);
      
    } catch (error) {
      console.error('Failed to send email:', error);
      if (error instanceof ApiError) {
        showError(error.message);
      } else {
        showError('Failed to send email. Please try again.');
      }
    } finally {
      setSending(false);
    }
  };

  const handlePreview = () => {
    if (!subject.trim()) {
      showError('Please enter a subject for the email.');
      return;
    }
    if (!htmlBody.trim()) {
      showError('Please enter content for the email.');
      return;
    }
    if (selectedUsers.length === 0) {
      showError('Please select at least one recipient.');
      return;
    }
    
    setIsPreviewModalOpen(true);
  };

  if (loading) {
    return (
      <div className="emails-container">
        <div className="loading-spinner">Loading users and companies...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="emails-container">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="emails-container">
      <div className="emails-layout">
        {/* Left sidebar - User selection (25%) */}
        <div className="emails-sidebar">
          <div className="sidebar-header">
            <h3>Select Recipients</h3>
            <div className="recipient-count">
              {selectedUsers.length} selected
            </div>
          </div>
          
          <div className="user-search">
            <input
              type="text"
              placeholder="Search users or companies..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="select-all-container">
            <label className="select-all-checkbox">
              <input
                type="checkbox"
                checked={selectAll}
                onChange={(e) => handleSelectAll(e.target.checked)}
              />
              <span>Select All ({filteredUsers.length})</span>
            </label>
          </div>

          <div className="users-list">
            {filteredUsers.map(user => (
              <div key={user.id} className={`user-item ${selectedUserIds.has(user.id) ? 'selected' : ''}`}>
                <label className="user-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedUserIds.has(user.id)}
                    onChange={(e) => handleUserSelect(user.id, e.target.checked)}
                  />
                  <div className="user-info">
                    <img src={getAvatarUrl(user.profileImageUrl, user.username)} alt={user.username} className="user-avatar" />
                    <div className="user-details">
                      <span className="user-name">{user.username}</span>
                      <span className="user-email">{user.email}</span>
                      <span className="user-company">{user.companyName}</span>
                    </div>
                  </div>
                </label>
              </div>
            ))}
            
            {filteredUsers.length === 0 && (
              <div className="no-users">No users found matching your search.</div>
            )}
          </div>
        </div>

        {/* Right content - Email composition (75%) */}
        <div className="emails-content">
          <div className="email-form">
            <div className="form-group">
              <label htmlFor="subject">Subject *</label>
              <input
                id="subject"
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Enter email subject..."
                className="subject-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="content">Message Content *</label>
              <div className="editor-container">
                <ReactQuill
                  theme="snow"
                  value={htmlBody}
                  onChange={setHtmlBody}
                  modules={modules}
                  formats={formats}
                  placeholder="Type your email content here..."
                />
              </div>
            </div>

            <div className="form-group">
              <label className="send-method-label">
                <input
                  type="checkbox"
                  checked={sendAsGroup}
                  onChange={(e) => setSendAsGroup(e.target.checked)}
                />
                <span>Send as group email (all recipients will see each other)</span>
              </label>
              <div className="send-method-help">
                {sendAsGroup 
                  ? 'All recipients will be in the "To" field and can see each other\'s email addresses.'
                  : 'Each recipient will receive a separate individual email.'
                }
              </div>
            </div>

            <div className="form-actions">
              <button 
                className="preview-button"
                onClick={handlePreview}
                disabled={!subject.trim() || !htmlBody.trim() || selectedUsers.length === 0}
              >
                Preview & Send
              </button>
              
              <div className="form-summary">
                Recipients: {selectedUsers.length} | 
                Method: {sendAsGroup ? 'Group' : 'Individual'} | 
                Subject: {subject || '(empty)'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Email Preview Modal */}
      <EmailPreviewModal
        isOpen={isPreviewModalOpen}
        onClose={() => setIsPreviewModalOpen(false)}
        subject={subject}
        htmlBody={htmlBody}
        selectedUsers={selectedUsers}
        sendAsGroup={sendAsGroup}
        onSend={handleSendEmail}
        sending={sending}
      />
    </div>
  );
};

export default Emails;